import {
  Icon,
  IconButton,
  ISelectOption,
  Modal,
  Select,
  theme,
  Tooltip,
  Alert,
  Gap,
} from '@salecycle/ui-component-library';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Block from '../common/Block';
import { usePermissions } from 'core/app/AppContext';
import { State as CampaignState } from 'entities/lifecycle/lifeCycle';
import { DelaySelector } from './DelaySelector';
import { maximumDelay } from '../../configs/sendConfig';
import {
  useOffsiteCampaignEditorDispatch,
  useOffsiteCampaignEditorState,
} from '../../context/UseOffsiteCampaignEditorContext';
import { ICycle } from '../../../entities/offsiteCampaign';
import { getInitialValues } from '../../../../../utils/timeUtils';
import { getFirstNonEmptyValue } from 'sections/utils/objectHelpers';
import { useQuery } from 'react-query';
import { getSubjectLines } from 'sections/design/template-management/sections/template-list/utils/queries';
import { millisToSpecificUnitValue } from 'core/ui/utils/unitMillis';
import { IntegrationChannel } from 'sections/client-management/ui/pages/client-settings/integrations/context/IntegrationsState';

const PreviewLink = styled(Link)`
  border: 1px solid transparent;
  color: ${({ theme }) => theme.vars.newColor.darkGS2};
  padding: 0.4rem 0.7rem;

  &:hover {
    background-color: ${({ theme }) => theme.vars.newColor.lightGS2};
    border-color: ${({ theme }) => theme.vars.newColor.lightGS1};
  }
`;

const List = styled.ul`
  list-style-type: none;
  margin-bottom: 0;
`;

const Item = styled.li``;

const Title = styled.span`
  font-weight: 600;
`;

interface OwnProps {
  bubbleDisabled?: boolean;
  cycleIndex: number;
  segmentId: string;
  splitId: string;
  cycle: ICycle;
  campaignState: CampaignState;
  siteId?: string;
}

export type Props = OwnProps;

export const CycleTemplate: FC<Props> = props => {
  const { t } = useTranslation();
  const dispatch = useOffsiteCampaignEditorDispatch();
  const { hasPermission } = usePermissions();
  const { campaign, templates, site } = useOffsiteCampaignEditorState();
  const template = templates.find(t => t.id === props.cycle.templateId) || null;
  const [offSiteModalOpen, setOffSiteModalOpen] = useState(false);

  const offSiteTemplateOptions = templates.reduce((acc, template) => {
    if (template.type === campaign.type && template.channel === props.cycle.messageType) {
      acc.push({
        value: template.id,
        label: template.templateName,
      });
    }
    return acc;
  }, []);

  const offSiteTemplate = offSiteTemplateOptions.find(t => t.value === props.cycle.templateId) || null;
  const [tempOffSiteTemplate, setOffSiteTemplate] = useState(offSiteTemplate);
  const onOffSiteTemplateChange = (option: ISelectOption<string>): void => {
    const templateObject = offSiteTemplateOptions.find(temp => temp.value === option.value);
    setOffSiteTemplate(templateObject);
  };

  const cycleDelay = props.cycle.delay;
  const [tempDelay, setDelay] = useState(cycleDelay);
  const onDelayChange = (interval: number) => {
    setDelay(interval);
  };

  const [subjectLines, setSubjectLines] = useState([]);
  const { isLoading, data } = useQuery(['subjectLines'], () => getSubjectLines({ siteId: site.id, t }));

  const subjectLineOptions = subjectLines.map(s => ({
    value: s.id,
    label: getFirstNonEmptyValue(s.subjectLines, 'en'),
  }));

  useEffect(() => {
    if (!isLoading) {
      setSubjectLines(data ?? []);
    }
  }, [isLoading, data]);

  const subjectLine = subjectLineOptions.find(s => s.value === props.cycle.subjectLineId) || null;
  const [tempSubjectLine, setTempSubjectLine] = useState(null);
  const onSubjectLineChange = (option: ISelectOption<string>): void => {
    const subjectLine = subjectLineOptions.find(s => s.value === option.value);
    setTempSubjectLine(subjectLine);
  };

  const showOffSiteModal = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    const offSiteTemplate = offSiteTemplateOptions.find(t => t.value === props.cycle.templateId) || null;
    setOffSiteTemplate(offSiteTemplate);
    const subjectLine = subjectLineOptions.find(s => s.value === props.cycle.subjectLineId) || null;
    setTempSubjectLine(subjectLine);
    const cycleDelay = props.cycle.delay;
    setDelay(cycleDelay);
    setOffSiteModalOpen(true);
  };

  const saveAndCloseOffSiteModal = () => {
    setOffSiteModalOpen(false);
    dispatch({
      type: 'UPDATE_CYCLE',
      payload: {
        cycle: {
          ...props.cycle,
          subjectLineId: tempSubjectLine?.value,
          templateId: tempOffSiteTemplate?.value,
          delay: tempDelay,
        },
        cycleId: props.cycle.id,
        segmentId: props.segmentId,
        splitId: props.splitId,
      },
    });
  };

  const CancelAndOffSiteCloseModal = () => {
    setOffSiteModalOpen(false);
  };

  const deleteCycle = () => {
    dispatch({
      type: 'REMOVE_CYCLE',
      payload: {
        cycleId: props.cycle.id,
        segmentId: props.segmentId,
        splitId: props.splitId,
      },
    });
  };

  const isValid = props.cycle.messageType === IntegrationChannel.WHATSAPP || !!props.cycle.templateId;
  const templateName = template?.templateName;
  const templateLanguage = template?.supportedLanguages?.[0]?.language ?? '';
  const sessionTime = site.sessionTime ? site.sessionTime : 0;

  const sessionTimeInMin = millisToSpecificUnitValue(sessionTime, 'minutes');
  const isSaveDisabled = (type: string): boolean => {
    return (
      (type === IntegrationChannel.EMAIL && !tempSubjectLine) ||
      tempDelay < sessionTime ||
      tempDelay > maximumDelay ||
      (type !== IntegrationChannel.WHATSAPP && !tempOffSiteTemplate)
    );
  };

  return (
    <div>
      <Modal
        variation="clean"
        isOpen={offSiteModalOpen}
        heading={t('campaigns:offsiteEditor.segments.cycleModal.heading', {
          type: t(`campaigns:channel.${props.cycle.messageType}`),
        })}
        okButtonLabel={t('campaigns:offsiteEditor.segments.cycleModal.okButton.label')}
        onOkButtonClicked={saveAndCloseOffSiteModal}
        okButtonDisabled={isSaveDisabled(props.cycle.messageType)}
        cancelButtonType="delete"
        cancelButtonLabel={t('campaigns:offsiteEditor.segments.cycleModal.cancelButton.label')}
        onCancelButtonClicked={CancelAndOffSiteCloseModal}
        size="large"
      >
        <Alert variation="info" heading={t('campaigns:alert.heading')}>
          {t('campaigns:alert.headingText', { sessionTimeInMin })}
        </Alert>
        <Gap bottom={20}></Gap>
        <DelaySelector
          delayInMillis={Math.max(props.cycle.delay, sessionTime)}
          sessionTimeout={sessionTime}
          onChange={onDelayChange}
        />
        {props.cycle.messageType !== IntegrationChannel.WHATSAPP && (
          <Select
            ariaLabel={t('campaigns:offsiteEditor.segments.cycles.template.templateIdLabelOffsite')}
            label={t('campaigns:offsiteEditor.segments.cycles.template.templateIdLabelOffsite')}
            value={tempOffSiteTemplate}
            options={offSiteTemplateOptions}
            onChange={onOffSiteTemplateChange}
            clearable={false}
            validationState={!!props.cycle.templateId ? 'complete' : 'warning'}
          />
        )}
        {props.cycle.messageType === IntegrationChannel.EMAIL && (
          <Select
            ariaLabel={t('campaigns:offsiteEditor.segments.cycles.template.templateAriaSubjectLine')}
            label={t('campaigns:offsiteEditor.segments.cycles.template.templateSubjectLine')}
            menuPlacement="top"
            value={tempSubjectLine}
            options={subjectLineOptions}
            onChange={onSubjectLineChange}
            clearable={false}
            validationState={!!tempSubjectLine ? 'complete' : 'warning'}
            validationMessage={
              !tempSubjectLine &&
              t('campaigns:offsiteEditor.segments.cycles.template.templateSubjectLineValidationMessage')
            }
          />
        )}
      </Modal>
      <Block
        icon="envelope"
        iconColor={theme.vars.newColor.blue2}
        campaignState={props.campaignState}
        title={t('campaigns:offsiteEditor.segments.cycles.label', {
          number: props.cycleIndex + 1,
          channel: t(`campaigns:channel.${props.cycle.messageType}`),
        })}
        value={templateName}
        valid={isValid}
        secondValue={
          props.cycle.delay >= sessionTime &&
          t('campaigns:offsiteEditor.segments.cycles.template.scheduled.delayedBody', {
            howMany: getInitialValues(props.cycle.delay).howMany,
            units: getInitialValues(props.cycle.delay).units,
          })
        }
        controls={
          <>
            <Tooltip title={t('campaigns:offsiteEditor.segments.cycles.editCycle.iconAriaLabel')} position="top">
              <IconButton
                ariaLabel={t('campaigns:offsiteEditor.segments.cycles.editCycle.iconAriaLabel')}
                icon="edit"
                onClick={showOffSiteModal}
                data-testid="cycle-modal-button"
              />
            </Tooltip>
            <>
              {hasPermission('ManageTemplates') && (
                <>
                  {!!props.cycle.templateId && (
                    <Tooltip title={t('campaigns:offsiteEditor.segments.cycles.template.preview')} position="top">
                      <PreviewLink
                        to={`/sites/${props.siteId}/designs/templates/${props.cycle.templateId}/${templateLanguage}/editor`}
                        target="_blank"
                        aria-describedby={t('campaigns:offsiteEditor.segments.cycles.template.preview')}
                        data-testid="TemplatePreview"
                      >
                        <Icon icon="code" />
                      </PreviewLink>
                    </Tooltip>
                  )}

                  {props.cycle.messageType === IntegrationChannel.EMAIL && (
                    <Tooltip title={t('campaigns:offsiteEditor.segments.cycles.subjectLine.preview')} position="top">
                      <PreviewLink
                        to={`/sites/${props.siteId}/designs/templates/email?tab=subject_lines`}
                        target="_blank"
                        aria-describedby={t('campaigns:offsiteEditor.segments.cycles.subjectLine.preview')}
                        data-testid="SubjectLinePreview"
                      >
                        <Icon icon="code" />
                      </PreviewLink>
                    </Tooltip>
                  )}
                </>
              )}
              <>
                <Tooltip title={t('campaigns:offsiteEditor.segments.cycles.delete.iconAriaLabel')} position="top">
                  <IconButton
                    ariaLabel={t('campaigns:offsiteEditor.segments.cycles.delete.iconAriaLabel')}
                    icon="trash"
                    onClick={deleteCycle}
                    data-testid="delete-cycle-button"
                  />
                </Tooltip>
              </>
            </>
          </>
        }
        details={
          <>
            {props.cycle.messageType === 'email' && (
              <List onClick={showOffSiteModal}>
                <Item data-testid="CampaignEditorDetailsSubjectTitle">
                  <Title>{t('campaigns:offsiteEditor.segments.cycles.template.templateSubjectLineTitle')}</Title>
                </Item>
                <Item data-testid="CampaignEditorDetailsSubjectValue">{subjectLine?.label}</Item>
              </List>
            )}
          </>
        }
        form={<></>}
        disableOpen={true}
      />
    </div>
  );
};
